/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — ui-chrome.ts
   Application shell: tab switching, dark mode, mobile menu, keyboard
   shortcuts, badges, empty states, and financial ratios dashboard.
   ("Chrome" = UI shell/frame surrounding the main content)
   ═══════════════════════════════════════════════════════════════════════ */

import {
  state,
  SECTIONS,
  type LineItem,
} from './state';
import { esc, fmt, sum, rows, cur, hasPriorData, el, elInput } from './utils';
import {
  buildIncomeStatement,
  buildBalanceSheet,
  buildCashFlow,
  buildEquityStatement,
  renderSuggestions,
} from './statements';
import { applyTemplateToStatements } from './templates';
import { generateAll, clearAll, exportToExcel, exportPackagePDF } from './export';
import { autoPopulateFromTB } from './notes';
import { runDisclosureChecklist } from './notes';

// ─── Tab Switching ──────────────────────────────────────────────────────────

const TAB_NAMES = ['input', 'balance', 'income', 'equity', 'cashflow', 'notes', 'checklist', 'ratios'];

export function switchTab(name: string): void {
  document.querySelectorAll('.page').forEach(function(p: Element) { p.classList.remove('active'); });
  document.querySelectorAll('.tab').forEach(function(t: Element) {
    t.classList.remove('active');
    t.setAttribute('aria-selected', 'false');
    t.setAttribute('tabindex', '-1');
  });
  el('page-' + name)?.classList.add('active');
  const tabs: Record<string, number> = { input: 0, balance: 1, income: 2, equity: 3, cashflow: 4, notes: 5, checklist: 6, ratios: 7 };
  const activeTab = document.querySelectorAll('.tab')[tabs[name]] as HTMLElement | undefined;
  if (activeTab) {
    activeTab.classList.add('active');
    activeTab.setAttribute('aria-selected', 'true');
    activeTab.setAttribute('tabindex', '0');
  }

  if (name === 'income') { buildIncomeStatement(); applyTemplateToStatements(); buildFinancialRatios(); }
  if (name === 'balance') { buildBalanceSheet(); applyTemplateToStatements(); buildFinancialRatios(); }
  if (name === 'cashflow') { buildCashFlow(); applyTemplateToStatements(); }
  if (name === 'equity') { buildEquityStatement(); applyTemplateToStatements(); }
  if (name === 'notes') { autoPopulateFromTB(); renderSuggestions(); }
  if (name === 'checklist') runDisclosureChecklist();
  if (name === 'ratios') { buildFinancialRatios(); }

  updateEmptyStates();
  updateTabBadges();

  // Focus management: move focus to the new panel's first heading or focusable element
  const panel = el('page-' + name);
  if (panel) {
    const focusTarget = panel.querySelector('h1, h2, h3, .card-title, button, input, select, textarea, [tabindex="0"]') as HTMLElement | null;
    if (focusTarget) {
      if (!focusTarget.hasAttribute('tabindex') && !['INPUT', 'BUTTON', 'SELECT', 'TEXTAREA', 'A'].includes(focusTarget.tagName)) {
        focusTarget.setAttribute('tabindex', '-1');
      }
      focusTarget.focus();
    }
  }
}

// ─── Mobile menu ────────────────────────────────────────────────────────────

export function toggleMobileMenu(): void {
  const menu = document.getElementById('mobile-menu');
  if (menu) menu.classList.toggle('open');
}

document.addEventListener('click', function(e: MouseEvent) {
  const menu = document.getElementById('mobile-menu');
  if (menu && menu.classList.contains('open') && !(e.target as Element).closest('.header-actions')) {
    menu.classList.remove('open');
  }
});

// ─── #5 Dark mode toggle ────────────────────────────────────────────────────

export function toggleDarkMode(): void {
  document.body.classList.toggle('dark-mode');
  localStorage.setItem('noteflow-dark-mode', document.body.classList.contains('dark-mode') ? '1' : '0');
}

(function initDarkMode(): void {
  if (localStorage.getItem('noteflow-dark-mode') === '1') {
    document.body.classList.add('dark-mode');
  }
})();

// ─── #9 Accordion note groups ─────────────────────────────────────────────

(function initAccordion(): void {
  document.querySelectorAll('.note-group').forEach(function(group: Element) {
    const title = group.querySelector('.note-group-title');
    if (!title) return;
    const body = document.createElement('div');
    body.className = 'note-group-body';
    const children = Array.from(group.children).filter(function(c: Element) { return c !== title; });
    children.forEach(function(c: Element) { body.appendChild(c); });
    group.appendChild(body);
    (body as HTMLElement).style.maxHeight = body.scrollHeight + 200 + 'px';
    title.addEventListener('click', function() {
      group.classList.toggle('collapsed');
      if (group.classList.contains('collapsed')) {
        (body as HTMLElement).style.maxHeight = '0';
      } else {
        (body as HTMLElement).style.maxHeight = body.scrollHeight + 200 + 'px';
      }
    });
  });
})();

// ─── #10 Confirm before clearing data ─────────────────────────────────────

export function confirmClearAll(): void {
  if (confirm('Are you sure you want to clear all imported data? This cannot be undone.')) {
    clearAll();
  }
}

// ─── #11 Keyboard shortcuts ───────────────────────────────────────────────

document.addEventListener('keydown', function(e: KeyboardEvent) {
  const appWrapper = document.getElementById('app-wrapper');
  const appVisible = appWrapper && appWrapper.classList.contains('visible');
  if (!appVisible) return;
  const inInput = (e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA' || (e.target as HTMLElement).tagName === 'SELECT';

  if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
    e.preventDefault(); generateAll();
  }
  if ((e.ctrlKey || e.metaKey) && e.key === 'e' && !inInput) {
    e.preventDefault(); exportToExcel();
  }
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'E') {
    e.preventDefault(); exportPackagePDF();
  }
  if (!inInput && !e.ctrlKey && !e.metaKey && !e.altKey) {
    const tabKeys: Record<string, string> = { '1': 'input', '2': 'balance', '3': 'income', '4': 'equity', '5': 'cashflow', '6': 'notes', '7': 'checklist', '8': 'ratios' };
    if (tabKeys[e.key]) { e.preventDefault(); switchTab(tabKeys[e.key]); }
  }

  // Arrow key navigation between tabs
  if ((e.target as HTMLElement).closest('[role="tab"]')) {
    const currentIdx = TAB_NAMES.indexOf(((e.target as HTMLElement).closest('[role="tab"]') as HTMLElement).dataset.param || '');
    if (currentIdx < 0) return;
    let newIdx = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      newIdx = (currentIdx + 1) % TAB_NAMES.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      newIdx = (currentIdx - 1 + TAB_NAMES.length) % TAB_NAMES.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      newIdx = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      newIdx = TAB_NAMES.length - 1;
    }
    if (newIdx >= 0) {
      switchTab(TAB_NAMES[newIdx]);
      const newTab = document.getElementById('tab-' + TAB_NAMES[newIdx]);
      if (newTab) newTab.focus();
    }
  }

  // Keyboard navigation for TB editor rows
  if ((e.target as HTMLElement).closest('.tb-item')) {
    const row = (e.target as HTMLElement).closest('.tb-item') as HTMLElement;
    if (e.key === 'Enter') {
      e.preventDefault();
      const editBtn = row.querySelector('[data-tb-action="edit"]') as HTMLElement | null;
      if (editBtn) editBtn.click();
    } else if (e.key === 'Delete') {
      e.preventDefault();
      const deleteBtn = row.querySelector('[data-tb-action="delete"]') as HTMLElement | null;
      if (deleteBtn) deleteBtn.click();
    }
  }
});

// ─── #3 Tab completion badges ─────────────────────────────────────────────

export function updateTabBadges(): void {
  const hasData = SECTIONS.some(function(s: string) { return state.currentData[s] && state.currentData[s].length > 0; });
  setBadge('badge-input', hasData ? 'complete' : 'none');
  setBadge('badge-balance', el('balance-statement')?.innerHTML.trim() ? 'complete' : 'none');
  setBadge('badge-income', el('income-statement')?.innerHTML.trim() ? 'complete' : 'none');
  setBadge('badge-equity', el('equity-statement')?.innerHTML.trim() ? 'complete' : 'none');
  setBadge('badge-cashflow', el('cashflow-statement')?.innerHTML.trim() ? 'complete' : 'none');
  const notesEl = el('notes-statement');
  const notesGenerated = notesEl && notesEl.innerHTML.trim();
  const notesWrapper = el('notes-output-wrapper');
  const notesVisible = notesWrapper && notesWrapper.style.display !== 'none';
  if (notesVisible && notesGenerated) {
    setBadge('badge-notes', 'complete');
  } else {
    const hasAnyNotes = document.querySelector('.notes-form textarea') &&
      Array.from(document.querySelectorAll('.notes-form textarea, .notes-form select, .notes-form input[type="text"]'))
        .some(function(el: Element) { return (el as HTMLInputElement).value && (el as HTMLInputElement).value.trim(); });
    setBadge('badge-notes', hasAnyNotes ? 'partial' : 'none');
  }
  const checklistEl = document.getElementById('checklist-output');
  const hasChecklist = checklistEl && checklistEl.querySelector('.checklist-item');
  setBadge('badge-checklist', hasChecklist ? 'complete' : 'none');
}

function setBadge(id: string, badgeState: string): void {
  const badge = document.getElementById(id);
  if (!badge) return;
  badge.classList.remove('active', 'partial');
  if (badgeState === 'complete') { badge.classList.add('active'); }
  else if (badgeState === 'partial') { badge.classList.add('active', 'partial'); }
}

// ─── #14 Empty state management ───────────────────────────────────────────

export function updateEmptyStates(): void {
  const stmts = ['balance', 'income', 'equity', 'cashflow'];
  stmts.forEach(function(name: string) {
    const stmtEl = el(name + '-statement');
    const emptyEl = el('empty-' + name);
    if (!stmtEl || !emptyEl) return;
    const hasContent = stmtEl.innerHTML.trim().length > 0;
    emptyEl.style.display = hasContent ? 'none' : 'block';
    stmtEl.style.display = hasContent ? 'block' : 'none';
  });
  const hasAnyData = SECTIONS.some(function(s: string) { return rows(state.currentData, s).length > 0; });
  const ratiosEmpty = el('empty-ratios');
  const ratiosDash = el('ratios-dashboard');
  if (ratiosEmpty) ratiosEmpty.style.display = hasAnyData ? 'none' : 'block';
  if (ratiosDash) ratiosDash.style.display = hasAnyData ? 'block' : 'none';
}

document.addEventListener('DOMContentLoaded', function() {
  setTimeout(updateEmptyStates, 100);
});

// ─── Financial Ratios & Analytics ─────────────────────────────────────────

export function buildFinancialRatios(): void {
  const multi = hasPriorData();

  function safeDiv(num: number, den: number): number | null {
    if (den === 0 || den === null || den === undefined) return null;
    return num / den;
  }
  function safePct(num: number, den: number): number | null {
    const r = safeDiv(num, den);
    return r === null ? null : r * 100;
  }

  function fmtRatio(val: number | null | undefined, suffix?: string): string {
    if (val === null || val === undefined || !isFinite(val)) return 'N/A';
    suffix = suffix || '';
    if (suffix === '%') return val.toFixed(1) + '%';
    if (suffix === '$') return cur() + fmt(val);
    return val.toFixed(2);
  }

  function ratioItem(name: string, curVal: number | null, priorVal: number | null | undefined, suffix: string, favorableUp: boolean): string {
    const curDisplay = fmtRatio(curVal, suffix);
    let html = '<div class="ratio-item"><div class="ratio-name">' + esc(name) + '</div>';
    html += '<div class="ratio-value">' + curDisplay + '</div>';
    if (multi && priorVal !== null && priorVal !== undefined && isFinite(priorVal)) {
      const priorDisplay = fmtRatio(priorVal, suffix);
      html += '<div class="ratio-prior">Prior: ' + priorDisplay + '</div>';
      if (curVal !== null && isFinite(curVal)) {
        const diff = curVal - priorVal;
        if (Math.abs(diff) > 0.001) {
          const isUp = diff > 0;
          const isFavorable = favorableUp ? isUp : !isUp;
          const arrow = isUp ? '\u2191' : '\u2193';
          const cls = isFavorable ? 'favorable' : 'unfavorable';
          html += '<div class="ratio-change ' + cls + '">' + arrow + ' ' + Math.abs(diff).toFixed(2) + (suffix === '%' ? '%' : '') + '</div>';
        }
      }
    }
    html += '</div>';
    return html;
  }

  // ── Balance Sheet Ratios ──
  const bsGrid = document.getElementById('balance-ratios-grid');
  const bsCard = document.getElementById('balance-ratios-card');
  let totalCA = 0, totalCL = 0, totalNCA = 0, totalNCL = 0, totalAssets = 0, totalLiab = 0, totalEquity = 0;
  let pCA = 0, pCL = 0, pNCA = 0, pNCL = 0, pAssets = 0, pLiab = 0, pEquity = 0;
  let inventoryAmt = 0, pInventoryAmt = 0;

  if (bsGrid && bsCard) {
    totalCA = sum(state.currentData, 'current-assets');
    totalCL = sum(state.currentData, 'current-liab');
    totalNCA = sum(state.currentData, 'noncurrent-assets');
    totalNCL = sum(state.currentData, 'noncurrent-liab');
    totalAssets = totalCA + totalNCA;
    totalLiab = totalCL + totalNCL;
    totalEquity = sum(state.currentData, 'equity');

    if (multi) {
      pCA = sum(state.priorData, 'current-assets');
      pCL = sum(state.priorData, 'current-liab');
      pNCA = sum(state.priorData, 'noncurrent-assets');
      pNCL = sum(state.priorData, 'noncurrent-liab');
      pAssets = pCA + pNCA;
      pLiab = pCL + pNCL;
      pEquity = sum(state.priorData, 'equity');
    }

    rows(state.currentData, 'current-assets').forEach(function(r: LineItem) {
      if (/inventor/i.test(r.label)) inventoryAmt += parseFloat(String(r.amount)) || 0;
    });
    if (multi) {
      rows(state.priorData, 'current-assets').forEach(function(r: LineItem) {
        if (/inventor/i.test(r.label)) pInventoryAmt += parseFloat(String(r.amount)) || 0;
      });
    }

    let bsHtml = '';
    bsHtml += ratioItem('Current Ratio', safeDiv(totalCA, totalCL), multi ? safeDiv(pCA, pCL) : null, '', true);
    bsHtml += ratioItem('Quick Ratio', safeDiv(totalCA - inventoryAmt, totalCL), multi ? safeDiv(pCA - pInventoryAmt, pCL) : null, '', true);
    bsHtml += ratioItem('Debt-to-Equity', safeDiv(totalLiab, totalEquity), multi ? safeDiv(pLiab, pEquity) : null, '', false);
    bsHtml += ratioItem('Working Capital', totalCA - totalCL, multi ? (pCA - pCL) : null, '$', true);
    bsHtml += ratioItem('Debt Ratio', safeDiv(totalLiab, totalAssets), multi ? safeDiv(pLiab, pAssets) : null, '', false);
    bsHtml += ratioItem('Equity Ratio', safeDiv(totalEquity, totalAssets), multi ? safeDiv(pEquity, pAssets) : null, '', true);
    bsGrid.innerHTML = bsHtml;

    bsCard.style.display = (totalAssets !== 0 || totalLiab !== 0) ? 'block' : 'none';
  }

  // ── Income Statement Ratios ──
  const isGrid = document.getElementById('income-ratios-grid');
  const isCard = document.getElementById('income-ratios-card');
  let revenue = 0, cogs = 0, grossProfit = 0, opex = 0, opIncome = 0, other = 0, netIncome = 0;
  let pRevenue = 0, pCogs = 0, pGP = 0, pOpex2 = 0, pOpInc = 0, pOther2 = 0, pNI = 0;

  if (isGrid && isCard) {
    revenue = sum(state.currentData, 'revenue');
    cogs = sum(state.currentData, 'cogs');
    grossProfit = revenue - cogs;
    opex = sum(state.currentData, 'opex');
    opIncome = grossProfit - opex;
    other = sum(state.currentData, 'other');
    netIncome = opIncome + other;

    if (multi) {
      pRevenue = sum(state.priorData, 'revenue');
      pCogs = sum(state.priorData, 'cogs');
      pGP = pRevenue - pCogs;
      pOpex2 = sum(state.priorData, 'opex');
      pOpInc = pGP - pOpex2;
      pOther2 = sum(state.priorData, 'other');
      pNI = pOpInc + pOther2;
    }

    let isHtml = '';
    isHtml += ratioItem('Gross Profit Margin', safePct(grossProfit, revenue), multi ? safePct(pGP, pRevenue) : null, '%', true);
    isHtml += ratioItem('Operating Margin', safePct(opIncome, revenue), multi ? safePct(pOpInc, pRevenue) : null, '%', true);
    isHtml += ratioItem('Net Profit Margin', safePct(netIncome, revenue), multi ? safePct(pNI, pRevenue) : null, '%', true);
    isHtml += ratioItem('Operating Expense Ratio', safePct(opex, revenue), multi ? safePct(pOpex2, pRevenue) : null, '%', false);
    isHtml += ratioItem('COGS Ratio', safePct(cogs, revenue), multi ? safePct(pCogs, pRevenue) : null, '%', false);
    isGrid.innerHTML = isHtml;

    isCard.style.display = (revenue !== 0) ? 'block' : 'none';
  }

  // ── Full Ratios Dashboard Page ──
  const dashboard = document.getElementById('ratios-dashboard');
  const emptyRatios = document.getElementById('empty-ratios');
  const hasAnyData = (totalCA || totalCL || totalNCA || totalNCL || revenue);
  if (dashboard && emptyRatios) {
    if (!hasAnyData) {
      dashboard.style.display = 'none';
      emptyRatios.style.display = 'block';
      return;
    }
    dashboard.style.display = 'block';
    emptyRatios.style.display = 'none';

    const rCompany = document.getElementById('ratios-company-name');
    const rPeriod = document.getElementById('ratios-period');
    if (rCompany) rCompany.textContent = (elInput('companyName')?.value || 'Company') + ' — Financial Ratios & Analytics';
    if (rPeriod) rPeriod.textContent = elInput('period')?.value || '';

    const scorecards = document.getElementById('ratios-scorecards');
    if (scorecards) {
      let scHtml = '';
      function scorecard(label: string, value: string): string {
        return '<div class="ratios-scorecard"><div class="ratios-scorecard-label">' + label + '</div><div class="ratios-scorecard-value">' + value + '</div></div>';
      }
      scHtml += scorecard('Current Ratio', safeDiv(totalCA, totalCL) !== null ? safeDiv(totalCA, totalCL)!.toFixed(2) : 'N/A');
      scHtml += scorecard('Net Margin', revenue ? (safePct(netIncome, revenue))!.toFixed(1) + '%' : 'N/A');
      scHtml += scorecard('Debt-to-Equity', totalEquity ? safeDiv(totalLiab, totalEquity)!.toFixed(2) : 'N/A');
      scHtml += scorecard('Working Capital', cur() + fmt(totalCA - totalCL));
      scorecards.innerHTML = scHtml;
    }

    const liqDiv = document.getElementById('ratios-liquidity');
    if (liqDiv) {
      let liqHtml = '';
      liqHtml += ratioItem('Current Ratio', safeDiv(totalCA, totalCL), multi ? safeDiv(pCA, pCL) : null, '', true);
      liqHtml += ratioItem('Quick Ratio', safeDiv(totalCA - inventoryAmt, totalCL), multi ? safeDiv(pCA - pInventoryAmt, pCL) : null, '', true);
      liqHtml += ratioItem('Working Capital', totalCA - totalCL, multi ? (pCA - pCL) : null, '$', true);
      liqDiv.innerHTML = liqHtml;
    }

    const profDiv = document.getElementById('ratios-profitability');
    if (profDiv) {
      let profHtml = '';
      profHtml += ratioItem('Gross Profit Margin', safePct(grossProfit, revenue), multi ? safePct(pGP, pRevenue) : null, '%', true);
      profHtml += ratioItem('Operating Margin', safePct(opIncome, revenue), multi ? safePct(pOpInc, pRevenue) : null, '%', true);
      profHtml += ratioItem('Net Profit Margin', safePct(netIncome, revenue), multi ? safePct(pNI, pRevenue) : null, '%', true);
      profHtml += ratioItem('Return on Assets', safePct(netIncome, totalAssets), multi ? safePct(pNI, pAssets) : null, '%', true);
      profHtml += ratioItem('Return on Equity', safePct(netIncome, totalEquity), multi ? safePct(pNI, pEquity) : null, '%', true);
      profDiv.innerHTML = profHtml;
    }

    const levDiv = document.getElementById('ratios-leverage');
    if (levDiv) {
      let levHtml = '';
      levHtml += ratioItem('Debt-to-Equity', safeDiv(totalLiab, totalEquity), multi ? safeDiv(pLiab, pEquity) : null, '', false);
      levHtml += ratioItem('Debt Ratio', safeDiv(totalLiab, totalAssets), multi ? safeDiv(pLiab, pAssets) : null, '', false);
      levHtml += ratioItem('Equity Ratio', safeDiv(totalEquity, totalAssets), multi ? safeDiv(pEquity, pAssets) : null, '', true);
      levDiv.innerHTML = levHtml;
    }

    const effDiv = document.getElementById('ratios-efficiency');
    if (effDiv) {
      let effHtml = '';
      let arAmt = 0, pArAmt = 0;
      rows(state.currentData, 'current-assets').forEach(function(r: LineItem) { if (/receiv/i.test(r.label)) arAmt += parseFloat(String(r.amount)) || 0; });
      if (multi) rows(state.priorData, 'current-assets').forEach(function(r: LineItem) { if (/receiv/i.test(r.label)) pArAmt += parseFloat(String(r.amount)) || 0; });
      let apAmt = 0, pApAmt = 0;
      rows(state.currentData, 'current-liab').forEach(function(r: LineItem) { if (/payable/i.test(r.label)) apAmt += parseFloat(String(r.amount)) || 0; });
      if (multi) rows(state.priorData, 'current-liab').forEach(function(r: LineItem) { if (/payable/i.test(r.label)) pApAmt += parseFloat(String(r.amount)) || 0; });
      effHtml += ratioItem('Asset Turnover', safeDiv(revenue, totalAssets), multi ? safeDiv(pRevenue, pAssets) : null, '', true);
      effHtml += ratioItem('AR Turnover', safeDiv(revenue, arAmt), multi ? safeDiv(pRevenue, pArAmt) : null, '', true);
      effHtml += ratioItem('Days Sales Outstanding', arAmt && revenue ? (arAmt / revenue * 365) : null, multi && pArAmt && pRevenue ? (pArAmt / pRevenue * 365) : null, '', false);
      effHtml += ratioItem('AP Turnover', safeDiv(cogs, apAmt), multi ? safeDiv(pCogs, pApAmt) : null, '', false);
      effDiv.innerHTML = effHtml;
    }

    const insightsCard = document.getElementById('ratios-insights-card');
    const insightsDiv = document.getElementById('ratios-insights');
    if (insightsCard && insightsDiv) {
      const insights: string[] = [];
      const cr = safeDiv(totalCA, totalCL);
      if (cr !== null && cr < 1) insights.push('<div style="padding:6px 0;color:var(--red)">\u26A0 Current ratio below 1.0 \u2014 potential liquidity risk.</div>');
      if (cr !== null && cr > 3) insights.push('<div style="padding:6px 0;color:var(--muted)">Current ratio above 3.0 \u2014 excess liquidity may indicate underutilized assets.</div>');
      const de = safeDiv(totalLiab, totalEquity);
      if (de !== null && de > 2) insights.push('<div style="padding:6px 0;color:var(--red)">\u26A0 Debt-to-equity above 2.0 \u2014 high leverage.</div>');
      const npm = safePct(netIncome, revenue);
      if (npm !== null && npm < 0) insights.push('<div style="padding:6px 0;color:var(--red)">\u26A0 Negative net profit margin \u2014 company is operating at a loss.</div>');
      if (npm !== null && npm > 20) insights.push('<div style="padding:6px 0;color:var(--green)">Strong net profit margin above 20%.</div>');
      if (multi) {
        const prevCR = safeDiv(pCA, pCL);
        if (cr !== null && prevCR !== null && cr < prevCR) insights.push('<div style="padding:6px 0;color:#f59e0b">Current ratio declining from prior period.</div>');
      }
      if (insights.length > 0) {
        (insightsCard as HTMLElement).style.display = 'block';
        insightsDiv.innerHTML = insights.join('');
      } else {
        (insightsCard as HTMLElement).style.display = 'none';
      }
    }
  }
}

// ─── Export Ratios PDF ──────────────────────────────────────────────────────

export function exportRatiosPDF(): void {
  if (typeof (window as any).jspdf === 'undefined' && typeof (window as any).jsPDF === 'undefined') {
    alert('PDF library not loaded yet. Please try again.');
    return;
  }
  const jsPDF = ((window as any).jspdf || {}).jsPDF || (window as any).jsPDF;
  const doc = new jsPDF();
  const company = elInput('companyName')?.value || 'Company';
  const period = elInput('period')?.value || '';
  doc.setFontSize(16);
  doc.text(company + ' - Financial Ratios', 14, 20);
  doc.setFontSize(10);
  if (period) doc.text(period, 14, 28);
  let yPos = 36;
  const sections = [
    { title: 'Liquidity Ratios', gridId: 'ratios-liquidity' },
    { title: 'Profitability Ratios', gridId: 'ratios-profitability' },
    { title: 'Leverage & Solvency Ratios', gridId: 'ratios-leverage' },
    { title: 'Efficiency & Activity Ratios', gridId: 'ratios-efficiency' },
    { title: 'Balance Sheet Ratios', gridId: 'balance-ratios-grid' },
    { title: 'Income Statement Ratios', gridId: 'income-ratios-grid' },
  ];
  sections.forEach(function(sec: { title: string; gridId: string }) {
    const grid = document.getElementById(sec.gridId);
    if (!grid || !grid.innerHTML.trim()) return;
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(sec.title, 14, yPos);
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    grid.querySelectorAll('.ratio-item').forEach(function(item: Element) {
      const nameEl = item.querySelector('.ratio-name');
      const valueEl = item.querySelector('.ratio-value');
      if (nameEl && valueEl) {
        doc.text(nameEl.textContent + ': ' + valueEl.textContent, 18, yPos);
        yPos += 6;
        if (yPos > 270) { doc.addPage(); yPos = 20; }
      }
    });
    yPos += 6;
  });
  doc.save(company.replace(/[^a-zA-Z0-9]/g, '_') + '_Ratios.pdf');
}
